import { getToken } from './auth-store.js';

/**
 * Thin fetch wrapper for the public API. Same-origin in dev (Vite proxies
 * `/v1/*` to the App at :3000); under a path prefix (e.g., `/xsc` for the
 * Wildix-nginx-shared-443 deploy), `VITE_BASE_PATH=/xsc` at build time
 * makes both api calls and SPA routes path-aware.
 *
 * Resolution order:
 *   VITE_API_BASE_URL (full URL override) > VITE_BASE_PATH (path prefix) > ''
 *
 * Phase 4+ enhancement: derive a typed client from the OpenAPI spec and
 * have routes import strongly-typed call sites (`api.events.list({...})`).
 * For now everything is `unknown` — we trust the App and let the UI cast.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const PATH_PREFIX = (import.meta.env.VITE_BASE_PATH as string | undefined) ?? '';
const PATH_PREFIX_NORMALIZED = PATH_PREFIX.replace(/\/$/, '');
const BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? PATH_PREFIX_NORMALIZED;

export async function apiRequest(
  method: string,
  path: string,
  body?: unknown,
): Promise<unknown> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(BASE_URL + path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    // Phase 4a — send the xsc_session cookie. Same-origin in production
    // (the App static-serves the SPA) so this is a no-op in that case;
    // matters only when the SPA dev server is on a different origin.
    credentials: 'include',
  });

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, parsed, `${method} ${path} failed (${res.status})`);
  }
  return parsed;
}

export const api = {
  get: <T = unknown>(path: string) => apiRequest('GET', path) as Promise<T>,
  post: <T = unknown>(path: string, body: unknown) => apiRequest('POST', path, body) as Promise<T>,
  put: <T = unknown>(path: string, body: unknown) => apiRequest('PUT', path, body) as Promise<T>,
  patch: <T = unknown>(path: string, body: unknown) => apiRequest('PATCH', path, body) as Promise<T>,
  delete: <T = unknown>(path: string) => apiRequest('DELETE', path) as Promise<T>,
};
